import React, { useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Alert, Form, Button, Label, TextLink } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import { useProductPlanMutations } from '../hooks/useProductPlanMutations';
import { useProductQuery } from '../hooks/useProductQuery';
import { useCreatePlanForProduct } from '../hooks/useCreatePlanForProduct';
import { ProductPlanDataTable } from './ProductPlanDataTable';
import { PlanCombobox } from './PlanCombobox';
import { CreatePlanModal } from './CreatePlanModal';
import type { PlanProduct } from '../types/plan.types';

interface Props {
    productId: string;
}

const attachSchema = z.object({
    plan_id: z.string().uuid('Select a plan'),
});

type AttachInput = z.infer<typeof attachSchema>;

export const ProductPlanPanel: React.FC<Props> = ({ productId }) => {
    const [showForm, setShowForm] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<PlanProduct | null>(null);

    const productQuery = useProductQuery(productId);
    const attachedPlans: PlanProduct[] = (productQuery.data?.plans ?? []) as PlanProduct[];
    const isLoading = productQuery.isLoading;

    const planMutations = useProductPlanMutations(productId);
    const { attachMutation, detachMutation } = planMutations;

    const attachedIds = attachedPlans.map((p) => p.id);

    const { handleSubmit, control, reset, formState: { isSubmitting } } = useForm<AttachInput>({
        resolver: zodResolver(attachSchema),
        defaultValues: { plan_id: '' },
    });

    const handleAttach = () => {
        handleSubmit((data) => {
            attachMutation.mutate(
                { planId: data.plan_id },
                {
                    onSuccess: () => {
                        reset();
                        setSelectedPlan(null);
                        setShowForm(false);
                    },
                },
            );
        })();
    };

    const handleCancel = () => {
        reset();
        setSelectedPlan(null);
        setShowForm(false);
    };

    const createPlan = useCreatePlanForProduct(planMutations, handleCancel);

    return (
        <div className="space-y-6">
            {isLoading ? (
                <ProductPlanDataTable data={[]} productId={productId} onRemove={() => {}} isLoading />
            ) : attachedPlans.length === 0 ? (
                <p className="text-mp-muted text-sm">No plans attached.</p>
            ) : (
                <ProductPlanDataTable
                    data={attachedPlans}
                    productId={productId}
                    onRemove={(planId) => detachMutation.mutate({ planId })}
                    removingPlanId={detachMutation.isPending ? (detachMutation.variables?.planId ?? null) : null}
                />
            )}

            <PermissionGuard permission={{ resource: 'smr', action: 'product.edit' }}>
                <div className="border-t border-mp-border pt-6 max-w-2xl">
                    {showForm ? (
                        <Form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleAttach(); }}>
                            <div>
                                <Label htmlFor="plan_search">Plan</Label>
                                <Controller
                                    name="plan_id"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <>
                                            <PlanCombobox
                                                id="plan_search"
                                                value={selectedPlan}
                                                onChange={(plan) => {
                                                    setSelectedPlan(plan);
                                                    field.onChange(plan?.id ?? '');
                                                }}
                                                excludeIds={attachedIds}
                                            />
                                            {error && <p className="text-mp-error text-xs mt-1">{error.message}</p>}
                                        </>
                                    )}
                                />
                            </div>

                            <PermissionGuard permission={{ resource: 'smr', action: 'plan.add' }}>
                                <p className="text-sm text-mp-muted">
                                    Not in the list?{' '}
                                    <TextLink
                                        onClick={createPlan.open}
                                        color="primary"
                                    >
                                        Create new plan
                                    </TextLink>
                                </p>
                            </PermissionGuard>

                            {createPlan.warning && (
                                <Alert variant="warning">
                                    <span>{createPlan.warning}</span>
                                </Alert>
                            )}

                            {attachMutation.isError && (
                                <Alert variant="error">
                                    <span>Failed to attach plan.</span>
                                </Alert>
                            )}

                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    type="button"
                                    onClick={handleAttach}
                                    disabled={isSubmitting || attachMutation.isPending || !selectedPlan}
                                >
                                    {attachMutation.isPending ? 'Attaching...' : 'Attach Plan'}
                                </Button>
                                <Button size="sm" variant="outline" type="button" onClick={handleCancel} disabled={attachMutation.isPending}>
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    ) : (
                        <div>
                            <h3 className="font-semibold mb-2">Add Plan</h3>
                            <div className="flex items-start gap-4 justify-between">
                                <p className="text-sm text-mp-muted">
                                    Attach a plan to this product.
                                </p>
                                <Button
                                    size="sm"
                                    type="button"
                                    variant="primary"
                                    onClick={() => setShowForm(true)}
                                    className="whitespace-nowrap"
                                >
                                    Add Plan
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </PermissionGuard>

            <CreatePlanModal
                isOpen={createPlan.isOpen}
                onClose={createPlan.close}
                onSubmit={createPlan.submit}
                isLoading={createPlan.isPending}
                error={createPlan.error}
                onSetErrorRef={createPlan.onSetErrorRef}
            />
        </div>
    );
};
